import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import axios from 'axios';
import styled from 'styled-components';

const APITestContainer = styled.div`
  max-width: 100%;
`;

const TestResults = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #eee;
`;

const TestList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
`;

const TestItem = styled.li`
  display: flex;
  align-items: center;
  margin: 0;
  font-size: 1rem;
  padding: 0.5rem;
  background: white;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
`;

const ErrorList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
  color: #dc3545;
`;

const ErrorItem = styled.li`
  margin: 0.5rem 0;
  padding: 0.75rem;
  background: #fff;
  border-left: 4px solid #dc3545;
  border-radius: 4px;
  font-size: 0.9rem;
`;

const TestSection = styled.div`
  margin-bottom: 1.5rem;

  h3 {
    margin: 0 0 1rem 0;
    color: var(--primary-color);
    font-size: 1.1rem;
  }
`;

const TestForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;

  button {
    padding: 0.5rem;
    background: var(--primary-color);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
      background: #357abd;
    }
  }
`;

const APITest = () => {
  const { currentUser } = useAuth();
  const [testResults, setTestResults] = useState({
    auth: {
      protected: { success: false, error: null },
      token: { success: false, error: null }
    },
    error: {
      handling: { success: false, error: null },
      validation: { success: false, error: null }
    },
    rate: {
      limiting: { success: false, error: null }
    },
    cors: {
      check: { success: false, error: null }
    }
  });

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

  const testProtectedEndpoint = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      setTestResults(prev => ({
        ...prev,
        auth: {
          ...prev.auth,
          protected: { success: false, error: 'User must be authenticated' }
        }
      }));
      return;
    }

    try {
      const idToken = await currentUser.getIdToken();
      const response = await axios.get(`${API_URL}/protected`, {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      });
      
      setTestResults(prev => ({
        ...prev,
        auth: {
          ...prev.auth,
          protected: { success: true, error: null }
        }
      }));
    } catch (error) {
      console.error('Protected endpoint test error:', error);
      setTestResults(prev => ({
        ...prev,
        auth: {
          ...prev.auth,
          protected: { 
            success: false, 
            error: error.response?.data?.message || error.message 
          }
        }
      }));
    }
  };

  const testTokenValidation = async (e) => {
    e.preventDefault();
    try {
      // Test with invalid token
      try {
        await axios.get(`${API_URL}/protected`, {
          headers: {
            Authorization: 'Bearer invalid_token'
          }
        });
      } catch (error) {
        if (error.response?.status === 401) {
          setTestResults(prev => ({
            ...prev,
            auth: {
              ...prev.auth,
              token: { success: true, error: null }
            }
          }));
          return;
        }
      }

      setTestResults(prev => ({
        ...prev,
        auth: {
          ...prev.auth,
          token: { 
            success: false, 
            error: 'Invalid token was accepted' 
          }
        }
      }));
    } catch (error) {
      console.error('Token validation test error:', error);
      setTestResults(prev => ({
        ...prev,
        auth: {
          ...prev.auth,
          token: { 
            success: false, 
            error: error.response?.data?.message || error.message 
          }
        }
      }));
    }
  };

  const testErrorHandling = async (e) => {
    e.preventDefault();
    try {
      // Test 404 handling
      try {
        await axios.get(`${API_URL}/nonexistent`);
      } catch (error) {
        if (error.response?.status === 404) {
          setTestResults(prev => ({
            ...prev,
            error: {
              ...prev.error,
              handling: { success: true, error: null }
            }
          }));
          return;
        }
      }

      setTestResults(prev => ({
        ...prev,
        error: {
          ...prev.error,
          handling: { 
            success: false, 
            error: '404 error was not properly handled' 
          }
        }
      }));
    } catch (error) {
      console.error('Error handling test error:', error);
      setTestResults(prev => ({
        ...prev,
        error: {
          ...prev.error,
          handling: { 
            success: false, 
            error: error.response?.data?.message || error.message 
          }
        }
      }));
    }
  };

  const testInputValidation = async (e) => {
    e.preventDefault();
    try {
      // Test with invalid input
      try {
        await axios.post(`${API_URL}/posts`, {
          title: '', // Empty title should be rejected
          content: 'Test content'
        });
      } catch (error) {
        if (error.response?.status === 400) {
          setTestResults(prev => ({
            ...prev,
            error: {
              ...prev.error,
              validation: { success: true, error: null }
            }
          }));
          return;
        }
      }

      setTestResults(prev => ({
        ...prev,
        error: {
          ...prev.error,
          validation: { 
            success: false, 
            error: 'Invalid input was accepted' 
          }
        }
      }));
    } catch (error) {
      console.error('Input validation test error:', error);
      setTestResults(prev => ({
        ...prev,
        error: {
          ...prev.error,
          validation: { 
            success: false, 
            error: error.response?.data?.message || error.message 
          }
        }
      }));
    }
  };

  const testRateLimiting = async (e) => {
    e.preventDefault();
    try {
      // Make multiple rapid requests
      const requests = Array(10).fill().map(() => 
        axios.get(`${API_URL}/posts`)
      );
      
      try {
        await Promise.all(requests);
        setTestResults(prev => ({
          ...prev,
          rate: {
            ...prev.rate,
            limiting: { 
              success: false, 
              error: 'Rate limiting not detected' 
            }
          }
        }));
      } catch (error) {
        if (error.response?.status === 429) {
          setTestResults(prev => ({
            ...prev,
            rate: {
              ...prev.rate,
              limiting: { success: true, error: null }
            }
          }));
          return;
        }
        throw error;
      }
    } catch (error) {
      console.error('Rate limiting test error:', error);
      setTestResults(prev => ({
        ...prev,
        rate: {
          ...prev.rate,
          limiting: { 
            success: false, 
            error: error.response?.data?.message || error.message 
          }
        }
      }));
    }
  };

  const testCORS = async (e) => {
    e.preventDefault();
    try {
      // Test CORS with different origin
      const response = await fetch(`${API_URL}/posts`, {
        method: 'OPTIONS',
        headers: {
          'Origin': 'http://example.com'
        }
      });
      
      const corsHeaders = response.headers.get('access-control-allow-origin');
      if (corsHeaders) {
        setTestResults(prev => ({
          ...prev,
          cors: {
            ...prev.cors,
            check: { success: true, error: null }
          }
        }));
      } else {
        setTestResults(prev => ({
          ...prev,
          cors: {
            ...prev.cors,
            check: { 
              success: false, 
              error: 'CORS headers not found' 
            }
          }
        }));
      }
    } catch (error) {
      console.error('CORS test error:', error);
      setTestResults(prev => ({
        ...prev,
        cors: {
          ...prev.cors,
          check: { 
            success: false, 
            error: error.message 
          }
        }
      }));
    }
  };

  return (
    <APITestContainer>
      <TestSection>
        <h3>API Integration Tests</h3>
        
        <TestForm onSubmit={testProtectedEndpoint}>
          <button type="submit">Test Protected Endpoint</button>
        </TestForm>

        <TestForm onSubmit={testTokenValidation}>
          <button type="submit">Test Token Validation</button>
        </TestForm>

        <TestForm onSubmit={testErrorHandling}>
          <button type="submit">Test Error Handling</button>
        </TestForm>

        <TestForm onSubmit={testInputValidation}>
          <button type="submit">Test Input Validation</button>
        </TestForm>

        <TestForm onSubmit={testRateLimiting}>
          <button type="submit">Test Rate Limiting</button>
        </TestForm>

        <TestForm onSubmit={testCORS}>
          <button type="submit">Test CORS Configuration</button>
        </TestForm>

        <TestResults>
          <h3>Test Results</h3>
          <TestList>
            <TestItem>Protected Endpoint: {testResults.auth.protected.success ? '✅' : '❌'}</TestItem>
            <TestItem>Token Validation: {testResults.auth.token.success ? '✅' : '❌'}</TestItem>
            <TestItem>Error Handling: {testResults.error.handling.success ? '✅' : '❌'}</TestItem>
            <TestItem>Input Validation: {testResults.error.validation.success ? '✅' : '❌'}</TestItem>
            <TestItem>Rate Limiting: {testResults.rate.limiting.success ? '✅' : '❌'}</TestItem>
            <TestItem>CORS Check: {testResults.cors.check.success ? '✅' : '❌'}</TestItem>
          </TestList>

          {(Object.values(testResults).some(category => 
            Object.values(category).some(test => test.error)
          )) && (
            <ErrorList>
              {testResults.auth.protected.error && <ErrorItem>Protected Endpoint: {testResults.auth.protected.error}</ErrorItem>}
              {testResults.auth.token.error && <ErrorItem>Token Validation: {testResults.auth.token.error}</ErrorItem>}
              {testResults.error.handling.error && <ErrorItem>Error Handling: {testResults.error.handling.error}</ErrorItem>}
              {testResults.error.validation.error && <ErrorItem>Input Validation: {testResults.error.validation.error}</ErrorItem>}
              {testResults.rate.limiting.error && <ErrorItem>Rate Limiting: {testResults.rate.limiting.error}</ErrorItem>}
              {testResults.cors.check.error && <ErrorItem>CORS Check: {testResults.cors.check.error}</ErrorItem>}
            </ErrorList>
          )}
        </TestResults>
      </TestSection>
    </APITestContainer>
  );
};

export default APITest; 